import { MoreVertical } from "lucide-react";
import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { ICON } from "./status.tsx";

/**
 * A set of actions behind one control.
 *
 * For a place that has more to offer than fits across a phone: the folder header carried five
 * buttons in a row and a sixth would have broken it, which is the whole reason this exists. One
 * target to aim at, and the actions readable as a list rather than as a cramped row.
 *
 * It is a real menu, not a div full of buttons. Hand-rolled because this app has no menu pattern
 * to copy and no component library to borrow one from, so the parts a menu is expected to have
 * are written out here once: the opener says it has a popup and whether it is open; the popup is
 * a `menu` of `menuitem`s; the arrows walk the items and wrap; Escape closes it and gives the
 * focus back to the opener, so a keyboard never ends up stranded; and a press anywhere else puts
 * it away rather than leaving it hanging over the content.
 *
 * Picking an item closes the menu and then does the thing. The menu is a way in, not a place to
 * work — a text field or a row of swatches inside a popover on a phone is worse than the same
 * thing with the width of the screen.
 */
export type MenuAction = {
  /** What it will do, in the fewest words that still say it: "Rename", "Share with the trip". */
  label: string;
  icon: ReactNode;
  onPick: () => void;
  /** A word under the label, when the label alone would leave somebody guessing. */
  detail?: string;
  /** Sets it apart below a rule: deleting a folder untags everybody's recordings. */
  destructive?: boolean;
};

export function ActionMenu({
  label,
  actions,
  floating = false,
  disabled = false,
}: {
  label: string;
  actions: MenuAction[];
  /**
   * Lay the list over the page instead of hanging it off the opener. For an opener inside
   * something that clips (a sheet with `overflow: hidden` would cut the list off): the list is
   * `fixed` at the opener's edge, below it when there is room and above it when there is not,
   * and put away if the page scrolls or resizes under it.
   */
  floating?: boolean;
  /** The opener does nothing: for while the thing it acts on is mid-write. */
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [where, setWhere] = useState<CSSProperties | undefined>(undefined);
  const id = useId();
  const opener = useRef<HTMLButtonElement>(null);
  const items = useRef<(HTMLButtonElement | null)[]>([]);

  // The first item takes the focus on opening, so the menu can be used without a pointer at all.
  useEffect(() => {
    if (open) items.current[0]?.focus({ preventScroll: true });
  }, [open]);

  // A press outside, not a click: a click arrives after the press has already moved the focus,
  // and on a touch screen the gap between the two is long enough to see.
  useEffect(() => {
    if (!open) return;
    const away = (e: Event) => {
      const t = e.target as Node | null;
      if (t && (opener.current?.contains(t) || items.current.some((i) => i?.contains(t)))) return;
      setOpen(false);
    };
    document.addEventListener("pointerdown", away);
    return () => document.removeEventListener("pointerdown", away);
  }, [open]);

  // A floating list is placed from where the opener is when it opens, and goes with any movement.
  useLayoutEffect(() => {
    if (!floating || !open || !opener.current) {
      setWhere(undefined);
      return;
    }
    const r = opener.current.getBoundingClientRect();
    const below = window.innerHeight - r.bottom;
    // Room for a few items under the opener; otherwise it opens upward from the opener's top.
    const down = below >= 220 || below >= r.top;
    setWhere({
      position: "fixed",
      right: Math.max(0, window.innerWidth - r.right),
      ...(down ? { top: r.bottom + 4 } : { bottom: window.innerHeight - r.top + 4 }),
    });
    const away = () => setOpen(false);
    window.addEventListener("resize", away);
    window.addEventListener("scroll", away, true);
    return () => {
      window.removeEventListener("resize", away);
      window.removeEventListener("scroll", away, true);
    };
  }, [floating, open]);

  function close(toOpener: boolean) {
    setOpen(false);
    if (toOpener) opener.current?.focus();
  }

  /** The arrows walk the items and wrap, so holding a direction never dead-ends. */
  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Escape") {
      e.stopPropagation();
      close(true);
      return;
    }
    const step = e.key === "ArrowDown" ? 1 : e.key === "ArrowUp" ? -1 : 0;
    if (step === 0) return;
    e.preventDefault();
    const list = items.current.filter((i): i is HTMLButtonElement => i !== null);
    const at = list.indexOf(document.activeElement as HTMLButtonElement);
    const next = (at + step + list.length) % list.length;
    list[next]?.focus();
  }

  if (actions.length === 0) return null;

  return (
    <div className="action-menu">
      <button
        ref={opener}
        type="button"
        className="action-menu-open"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={disabled}
        aria-controls={open ? id : undefined}
        onClick={() => setOpen((o) => !o)}
      >
        <MoreVertical {...ICON} aria-hidden="true" />
      </button>
      {open && (
        // The keys are handled on the container rather than on each item: a menu owns its own
        // keyboard, and the handler then works wherever inside it the focus happens to sit.
        <div
          className="action-menu-list"
          role="menu"
          id={id}
          style={where}
          aria-label={label}
          onKeyDown={onKeyDown}
        >
          {actions.map((a, i) => (
            <button
              key={a.label}
              ref={(el) => {
                items.current[i] = el;
              }}
              type="button"
              role="menuitem"
              className="action-menu-item"
              data-destructive={a.destructive === true}
              // Not tracked by the roving index a desktop menu would use: every item here is
              // reachable in one step, and a tab stop per item is what a phone expects.
              onClick={() => {
                // Closed before the action runs, so the thing it reveals is not behind a menu.
                close(false);
                a.onPick();
              }}
            >
              {a.icon}
              <span className="action-menu-text">
                <span>{a.label}</span>
                {a.detail && <span className="action-menu-detail">{a.detail}</span>}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
